import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, Home, PiggyBank, ShieldCheck, PlusCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета ипотеки
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета ипотеки">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Ежемесячный платеж */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4"/>
            Ежемесячный платеж*
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {/* Показываем диапазон для дифференцированных платежей, иначе первый платеж */}
              {Math.abs(results.firstTotalPayment - results.lastTotalPayment) > 0.01
                ? `${formatCurrency(results.firstTotalPayment)} → ${formatCurrency(results.lastTotalPayment)}`
                : formatCurrency(results.firstTotalPayment)
              }
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            *Включает: осн. долг, проценты
            {results.totalInsurance > 0 ? ', страх.' : ''}
            {results.totalOtherCosts > 0 ? ', разов. расх. (в 1м мес.)' : ''}
            {results.totalEarlyRepayment > 0 ? ', досроч. пог.' : ''}.
            {Math.abs(results.firstTotalPayment - results.lastTotalPayment) > 0.01 ? ' Платеж уменьшается со временем.' : ''}
          </p>
        </div>

        {/* Стоимость кредита */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Стоимость кредита
          </h3>
          <div className="text-lg font-semibold text-emerald-800 dark:text-emerald-200" aria-live="polite">
            Сумма кредита: {formatCurrency(results.loanAmount)}
          </div>
          <div className="text-lg font-semibold text-red-700 dark:text-red-400 mt-1" aria-live="polite">
            Переплата (%% + доп. расходы): {formatCurrency(results.totalCreditCost)}
          </div>
          <div className="text-lg font-semibold text-emerald-800 dark:text-emerald-200 mt-1" aria-live="polite">
            Общая сумма выплат: {formatCurrency(results.totalPayment)}
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            LTV: {formatNumber(results.loanToValue, 1)}%. 
            Переплата {formatNumber(results.overpaymentRatio * 100, 0)}% от суммы кредита.
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Переплата по процентам" 
          value={formatCurrency(results.totalInterest)} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        {results.totalInsurance > 0 && (
          <ResultItem 
            label="Расходы на страхование (всего)" 
            value={formatCurrency(results.totalInsurance)} 
            icon={<ShieldCheck className="w-4 h-4"/>} 
          />
        )}
        
        {results.totalOtherCosts > 0 && (
          <ResultItem 
            label="Разовые расходы при оформлении" 
            value={formatCurrency(results.totalOtherCosts)} 
            icon={<PlusCircle className="w-4 h-4"/>} 
          />
        )}
        
        {results.totalEarlyRepayment > 0 && (
          <ResultItem 
            label="Досрочно погашено (всего)" 
            value={formatCurrency(results.totalEarlyRepayment)} 
            color="green" 
            icon={<PiggyBank className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Фактический срок погашения" 
          value={`${Math.floor(results.paybackPeriodMonths / 12)} лет ${results.paybackPeriodMonths % 12} мес.`} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Стоимость недвижимости" 
          value={formatCurrency(results.propertyPrice)} 
          icon={<Home className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Первоначальный взнос" 
          value={`${formatCurrency(results.initialPayment)} (${formatNumber(100 - results.loanToValue, 1)}%)`} 
          icon={<PiggyBank className="w-4 h-4 text-blue-500"/>} 
        />
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;